import type { Judge, JudgeAssignmentRow } from "../types/database";
import { logAdminAction } from "./auditLog";
import { createInvite, type InviteRow } from "./invites";
import { getImpersonatedUserId } from "./impersonate";
import { supabase } from "./supabase";

type JudgeWithCompetition = Judge & {
  competitions:
    | { name: string; companies: { name: string } | { name: string }[] | null }
    | { name: string; companies: { name: string } | { name: string }[] | null }[]
    | null;
};

type SubmissionCountRow = {
  id: string;
  competition_id: string;
};

type SubmissionScoreCountRow = {
  id: string;
  competition_id: string;
};

function unwrapOne<T>(rel: T | T[] | null | undefined): T | null {
  if (rel == null) return null;
  return Array.isArray(rel) ? (rel[0] ?? null) : rel;
}

// ─── Admin-side judges directory ──────────────────────────────────────

export type AdminJudgeRow = {
  /** Stable id for routing — user_id when the judge has a profile,
   *  judges.id (the row in the judges table) otherwise. */
  id: string;
  user_id: string | null;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
  organization: string | null;
  headline: string | null;
  /**
   * "active"        — has a judge_profiles row, is_available = true
   * "inactive"      — has a judge_profiles row, is_available = false
   * "pending_invite" — judges row with user_id is null (invite outstanding)
   */
  status: "active" | "inactive" | "pending_invite";
  assigned_count: number;
  scored_count: number;
  last_activity_at: string | null;
};

export type AdminJudgeAssignmentRow = {
  judge_id: string;
  competition_id: string;
  competition_name: string;
  company_name: string | null;
  status: "invited" | "active" | "removed";
  scored_count: number;
  total_assigned: number;
  invited_at: string | null;
};

export type AdminJudgeActivityRow = {
  submission_score_id: string;
  competition_id: string;
  competition_name: string;
  status: "draft" | "submitted";
  score: number | null;
  updated_at: string;
};

export type AdminJudgeDetail = {
  user_id: string | null;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
  organization: string | null;
  headline: string | null;
  bio: string | null;
  linkedin_url: string | null;
  areas_of_expertise: string[];
  is_available: boolean;
  status: AdminJudgeRow["status"];
  assignments: AdminJudgeAssignmentRow[];
  activity: AdminJudgeActivityRow[];
};

/**
 * Roster of every judge known to the platform — judge_profiles rows
 * (post-acceptance) unioned with `judges` rows that don't yet have a
 * profile (status='invited' / user_id is null).
 *
 * Aggregates two counts per judge:
 *   • assigned_count — competitions where this user has at least one
 *     `judges` row, regardless of status.
 *   • scored_count — distinct submissions this user has finalised
 *     (`submission_scores.status='submitted'`).
 *
 * Last-activity is the most recent `submission_scores.updated_at`
 * across any of this user's judge rows. We don't peek at auth.users
 * (last_sign_in_at is service-role-only) — score activity is the
 * meaningful signal anyway.
 */
export async function listAllJudgesForAdmin(): Promise<AdminJudgeRow[]> {
  const [
    { data: profileRows },
    { data: judgesRows },
    { data: scoresRows },
    { data: profileEmailRows },
  ] = await Promise.all([
    supabase
      .from("judge_profiles")
      .select("user_id, headline, organization, is_available, updated_at"),
    supabase.from("judges").select("id, user_id, email, competition_id, status, invited_at"),
    supabase
      .from("submission_scores")
      .select("judge_id, status, updated_at")
      .eq("status", "submitted"),
    supabase.from("user_profiles").select("id, full_name, email, avatar_url"),
  ]);

  type ProfRow = {
    user_id: string;
    headline: string | null;
    organization: string | null;
    is_available: boolean;
    updated_at: string | null;
  };
  type JudgeAssignRow = {
    id: string;
    user_id: string | null;
    email: string;
    competition_id: string;
    status: "invited" | "active" | "removed";
    invited_at: string | null;
  };
  type ScoreRow = { judge_id: string; status: string; updated_at: string };
  type UPRow = { id: string; full_name: string | null; email: string | null; avatar_url: string | null };

  const profById = new Map<string, ProfRow>();
  for (const r of (profileRows ?? []) as ProfRow[]) profById.set(r.user_id, r);

  const userProfileById = new Map<string, UPRow>();
  for (const r of (profileEmailRows ?? []) as UPRow[]) userProfileById.set(r.id, r);

  // Map user_id → assigned competition count + last activity.
  const assignedByUser = new Map<string, Set<string>>();
  const judgeIdToUserId = new Map<string, string>();
  // For pending invites (user_id == null) we still want to surface them.
  const pendingByEmail = new Map<string, JudgeAssignRow>();
  for (const r of (judgesRows ?? []) as JudgeAssignRow[]) {
    if (r.user_id) {
      judgeIdToUserId.set(r.id, r.user_id);
      const set = assignedByUser.get(r.user_id) ?? new Set<string>();
      set.add(r.competition_id);
      assignedByUser.set(r.user_id, set);
    } else if (r.status === "invited") {
      // Keep just one row per email for the directory listing.
      const existing = pendingByEmail.get(r.email);
      if (!existing || (r.invited_at ?? "") > (existing.invited_at ?? "")) {
        pendingByEmail.set(r.email, r);
      }
    }
  }

  const scoresByUser = new Map<string, { count: number; latest: string | null }>();
  for (const s of (scoresRows ?? []) as ScoreRow[]) {
    const uid = judgeIdToUserId.get(s.judge_id);
    if (!uid) continue;
    const cur = scoresByUser.get(uid) ?? { count: 0, latest: null };
    cur.count += 1;
    if (!cur.latest || s.updated_at > cur.latest) cur.latest = s.updated_at;
    scoresByUser.set(uid, cur);
  }

  const out: AdminJudgeRow[] = [];

  // Profiled judges first.
  for (const [userId, p] of profById) {
    const up = userProfileById.get(userId);
    const score = scoresByUser.get(userId);
    out.push({
      id: userId,
      user_id: userId,
      full_name: up?.full_name ?? null,
      email: up?.email ?? "",
      avatar_url: up?.avatar_url ?? null,
      organization: p.organization,
      headline: p.headline,
      status: p.is_available ? "active" : "inactive",
      assigned_count: assignedByUser.get(userId)?.size ?? 0,
      scored_count: score?.count ?? 0,
      last_activity_at: score?.latest ?? p.updated_at ?? null,
    });
  }

  // Pending invites — only include if no profile exists for this email.
  const profiledEmails = new Set(out.map((r) => (r.email ?? "").toLowerCase()).filter(Boolean));
  for (const r of pendingByEmail.values()) {
    if (profiledEmails.has(r.email.toLowerCase())) continue;
    out.push({
      id: r.id,
      user_id: null,
      full_name: null,
      email: r.email,
      avatar_url: null,
      organization: null,
      headline: null,
      status: "pending_invite",
      assigned_count: 1,
      scored_count: 0,
      last_activity_at: r.invited_at,
    });
  }

  out.sort((a, b) => {
    const an = (a.full_name ?? a.email).toLowerCase();
    const bn = (b.full_name ?? b.email).toLowerCase();
    return an.localeCompare(bn);
  });
  return out;
}

export async function getAdminJudgeDetail(userId: string): Promise<AdminJudgeDetail | null> {
  const [
    { data: profile },
    { data: userProfile },
    { data: judges },
    { data: comps },
    { data: scores },
  ] = await Promise.all([
    supabase
      .from("judge_profiles")
      .select("user_id, headline, organization, bio, linkedin_url, areas_of_expertise, is_available")
      .eq("user_id", userId)
      .maybeSingle(),
    supabase
      .from("user_profiles")
      .select("id, full_name, email, avatar_url")
      .eq("id", userId)
      .maybeSingle(),
    supabase
      .from("judges")
      .select("id, competition_id, status, invited_at")
      .eq("user_id", userId)
      .order("invited_at", { ascending: false }),
    supabase.from("competitions").select("id, name, company_id"),
    supabase
      .from("submission_scores")
      .select("id, competition_id, status, score, updated_at, judge_id")
      .order("updated_at", { ascending: false })
      .limit(50),
  ]);

  if (!userProfile) return null;

  type JudgeRow = { id: string; competition_id: string; status: "invited" | "active" | "removed"; invited_at: string | null };
  type CompRow = { id: string; name: string; company_id: string };
  type ScoreRow = {
    id: string;
    competition_id: string;
    status: "draft" | "submitted";
    score: number | null;
    updated_at: string;
    judge_id: string;
  };

  const judgeRows = (judges ?? []) as JudgeRow[];
  const judgeIdSet = new Set(judgeRows.map((j) => j.id));
  const compById = new Map<string, CompRow>();
  for (const c of (comps ?? []) as CompRow[]) compById.set(c.id, c);

  // Hydrate company names for the assigned competitions.
  const companyIds = Array.from(
    new Set(judgeRows.map((j) => compById.get(j.competition_id)?.company_id).filter(Boolean) as string[])
  );
  const companyNameById = new Map<string, string>();
  if (companyIds.length > 0) {
    const { data: companies } = await supabase.from("companies").select("id, name").in("id", companyIds);
    for (const r of (companies ?? []) as { id: string; name: string }[]) companyNameById.set(r.id, r.name);
  }

  // Per-competition score counts and total submissions assigned for the
  // "X / Y scored" progress indicator.
  const scoreCountByJudge = new Map<string, number>();
  for (const s of (scores ?? []) as ScoreRow[]) {
    if (!judgeIdSet.has(s.judge_id) || s.status !== "submitted") continue;
    scoreCountByJudge.set(s.judge_id, (scoreCountByJudge.get(s.judge_id) ?? 0) + 1);
  }
  const subsByCompetition = new Map<string, number>();
  if (judgeRows.length > 0) {
    const compIds = judgeRows.map((j) => j.competition_id);
    const { data: subs } = await supabase
      .from("submissions")
      .select("competition_id")
      .in("competition_id", compIds);
    for (const s of (subs ?? []) as { competition_id: string }[]) {
      subsByCompetition.set(s.competition_id, (subsByCompetition.get(s.competition_id) ?? 0) + 1);
    }
  }

  const assignments: AdminJudgeAssignmentRow[] = judgeRows.map((j) => {
    const comp = compById.get(j.competition_id);
    return {
      judge_id: j.id,
      competition_id: j.competition_id,
      competition_name: comp?.name ?? "Competition",
      company_name: comp?.company_id ? companyNameById.get(comp.company_id) ?? null : null,
      status: j.status,
      scored_count: scoreCountByJudge.get(j.id) ?? 0,
      total_assigned: subsByCompetition.get(j.competition_id) ?? 0,
      invited_at: j.invited_at,
    };
  });

  const activity: AdminJudgeActivityRow[] = ((scores ?? []) as ScoreRow[])
    .filter((s) => judgeIdSet.has(s.judge_id))
    .slice(0, 10)
    .map((s) => ({
      submission_score_id: s.id,
      competition_id: s.competition_id,
      competition_name: compById.get(s.competition_id)?.name ?? "Competition",
      status: s.status,
      score: s.score,
      updated_at: s.updated_at,
    }));

  type ProfRow = {
    user_id: string;
    headline: string | null;
    organization: string | null;
    bio: string | null;
    linkedin_url: string | null;
    areas_of_expertise: string[];
    is_available: boolean;
  };
  const p = profile as ProfRow | null;
  const status: AdminJudgeRow["status"] = p ? (p.is_available ? "active" : "inactive") : "pending_invite";

  return {
    user_id: userId,
    full_name: (userProfile as { full_name: string | null }).full_name ?? null,
    email: (userProfile as { email: string | null }).email ?? "",
    avatar_url: (userProfile as { avatar_url: string | null }).avatar_url ?? null,
    organization: p?.organization ?? null,
    headline: p?.headline ?? null,
    bio: p?.bio ?? null,
    linkedin_url: p?.linkedin_url ?? null,
    areas_of_expertise: Array.isArray(p?.areas_of_expertise) ? p!.areas_of_expertise : [],
    is_available: p?.is_available ?? false,
    status,
    assignments,
    activity,
  };
}

export async function listJudges(competitionId: string): Promise<Judge[]> {
  const { data, error } = await supabase
    .from("judges")
    .select("*")
    .eq("competition_id", competitionId)
    .order("invited_at", { ascending: true });
  if (error || !data) return [];
  return data as Judge[];
}

export async function inviteJudge(params: {
  competitionId: string;
  email: string;
  can_invite_others: boolean;
  can_change_deadlines: boolean;
  scope_majors: string[] | null;
}): Promise<{ judge: Judge; invite: InviteRow }> {
  const { data: judge, error } = await supabase
    .from("judges")
    .insert({
      competition_id: params.competitionId,
      email: params.email.trim().toLowerCase(),
      status: "invited",
      user_id: null,
      can_invite_others: params.can_invite_others,
      can_change_deadlines: params.can_change_deadlines,
      scope_majors: params.scope_majors,
    })
    .select("*")
    .single();
  if (error || !judge) throw new Error(error?.message ?? "Failed to create judge");
  const invite = await createInvite({
    kind: "judge",
    email: params.email,
    competitionId: params.competitionId,
    judgeId: judge.id,
  });
  // createInvite logs the invite creation separately. The judge_assignment
  // row itself is the audit-relevant event here — assigning a judge to a
  // competition has security implications (they get to see all submissions
  // for the comp), so it gets its own action distinct from invite.create.
  await logAdminAction({
    action: "judge_assignment.create",
    entityType: "judges",
    entityId: judge.id as string,
    summary: `Assigned judge ${params.email.trim().toLowerCase()} to competition`,
    diff: {
      after: {
        competition_id: params.competitionId,
        email: params.email.trim().toLowerCase(),
        can_invite_others: params.can_invite_others,
        can_change_deadlines: params.can_change_deadlines,
      },
    },
  });
  return { judge: judge as Judge, invite };
}

export async function updateJudgePermissions(
  id: string,
  patch: Partial<Pick<Judge, "can_invite_others" | "can_change_deadlines" | "scope_majors" | "status">>
): Promise<{ error: Error | null }> {
  const { error } = await supabase.from("judges").update(patch).eq("id", id);
  return { error: error ? new Error(error.message) : null };
}

export async function removeJudge(id: string): Promise<{ error: Error | null }> {
  // Use updateJudgePermissions to flip status='removed' (preserves the row
  // so historical scores stay attributable). updateJudgePermissions doesn't
  // self-log because most permission edits are noisy; we add the
  // judge_assignment.delete entry here so the audit trail records the
  // significant "judge no longer assigned" event.
  const result = await updateJudgePermissions(id, { status: "removed" });
  if (result.error) return result;
  await logAdminAction({
    action: "judge_assignment.delete",
    entityType: "judges",
    entityId: id,
    summary: `Removed judge from competition`,
    diff: { after: { status: "removed" } },
  });
  return result;
}

export async function getMyJudgeAssignments(): Promise<JudgeAssignmentRow[]> {
  // Follow the view-as target when an admin is impersonating a judge.
  let uid = getImpersonatedUserId();
  if (!uid) {
    const { data: userData, error: userErr } = await supabase.auth.getUser();
    if (userErr || !userData.user) return [];
    uid = userData.user.id;
  }

  const { data: judgeRows, error } = await supabase
    .from("judges")
    .select("*, competitions(name, companies(name))")
    .eq("user_id", uid)
    .eq("status", "active");
  if (error || !judgeRows) return [];

  const judges = judgeRows as JudgeWithCompetition[];
  const competitionIds = judges.map((j) => j.competition_id);
  if (competitionIds.length === 0) return [];

  const [{ data: submissions }, { data: scoreRows }] = await Promise.all([
    supabase.from("submissions").select("id, competition_id").in("competition_id", competitionIds),
    supabase
      .from("submission_scores")
      .select("id, competition_id")
      .in("competition_id", competitionIds)
      .in("judge_id", judges.map((j) => j.id)),
  ]);

  const totalByComp = new Map<string, number>();
  for (const row of (submissions ?? []) as SubmissionCountRow[]) {
    totalByComp.set(row.competition_id, (totalByComp.get(row.competition_id) ?? 0) + 1);
  }

  const scoredByComp = new Map<string, number>();
  for (const row of (scoreRows ?? []) as SubmissionScoreCountRow[]) {
    scoredByComp.set(row.competition_id, (scoredByComp.get(row.competition_id) ?? 0) + 1);
  }

  const out: JudgeAssignmentRow[] = judges.map((j) => {
    const comp = unwrapOne(j.competitions);
    const company = comp ? unwrapOne(comp.companies) : null;
    const scored = scoredByComp.get(j.competition_id) ?? 0;
    const total = totalByComp.get(j.competition_id) ?? 0;
    return {
      competition_id: j.competition_id,
      competition_name: comp?.name ?? "Competition",
      company_name: company?.name ?? "Company",
      pending_count: Math.max(0, total - scored),
      scored_count: scored,
    };
  });

  out.sort((a, b) => b.pending_count - a.pending_count || a.competition_name.localeCompare(b.competition_name));
  return out;
}

// ─── Submission-level queue (per-row assignments) ──────────────────────

export type JudgeSubmissionQueueRow = {
  submission_id: string;
  competition_id: string;
  competition_name: string;
  company_name: string;
  deliverable_id: string;
  deliverable_name: string;
  deliverable_sequence: number;
  deliverable_is_locked_gate: boolean;
  deliverable_due_at: string | null;
  team_id: string;
  team_name: string;
  file_url: string;
  file_name: string;
  file_size_bytes: number | null;
  submitted_at: string | null;
  /** Score state if the judge already touched this submission. */
  my_score: number | null;
  my_passed_gate: boolean | null;
  my_score_status: "draft" | "submitted" | null;
  my_score_updated_at: string | null;
};

type RawSubmissionRow = {
  id: string;
  deliverable_id: string;
  team_id: string;
  competition_id: string;
  file_url: string;
  file_name: string;
  file_size_bytes: number | null;
  submitted_at: string | null;
  deliverables:
    | { name: string; sequence: number; is_locked_gate: boolean; due_at: string | null }
    | { name: string; sequence: number; is_locked_gate: boolean; due_at: string | null }[]
    | null;
  teams: { name: string } | { name: string }[] | null;
};

type RawScoreRow = {
  submission_id: string;
  score: number | null;
  passed_gate: boolean | null;
  status: "draft" | "submitted" | null;
  updated_at: string | null;
  judge_id: string;
};

/**
 * Flat list of every submission this judge can score across every active
 * assignment. Returns one row per submission. The /judge home page groups
 * client-side (Due soon / All / Completed).
 */
export async function listMyJudgeSubmissions(): Promise<JudgeSubmissionQueueRow[]> {
  // Follow the view-as target when an admin is impersonating a judge.
  let uid = getImpersonatedUserId();
  if (!uid) {
    const { data: userData, error: userErr } = await supabase.auth.getUser();
    if (userErr || !userData.user) return [];
    uid = userData.user.id;
  }

  // 1. Active judge rows for this user, with competition + company embedded.
  const { data: judgeRows, error: judgeErr } = await supabase
    .from("judges")
    .select("id, competition_id, competitions(name, companies(name))")
    .eq("user_id", uid)
    .eq("status", "active");
  if (judgeErr || !judgeRows) return [];

  type JRow = {
    id: string;
    competition_id: string;
    competitions:
      | { name: string; companies: { name: string } | { name: string }[] | null }
      | { name: string; companies: { name: string } | { name: string }[] | null }[]
      | null;
  };

  const judges = judgeRows as JRow[];
  if (judges.length === 0) return [];

  const competitionIds = judges.map((j) => j.competition_id);
  const judgeIds = judges.map((j) => j.id);

  const compMeta = new Map<string, { competition_name: string; company_name: string }>();
  for (const j of judges) {
    const comp = unwrapOne(j.competitions);
    const company = comp ? unwrapOne(comp.companies) : null;
    compMeta.set(j.competition_id, {
      competition_name: comp?.name ?? "Competition",
      company_name: company?.name ?? "Company",
    });
  }

  // 2. All submissions for those competitions + the current user's scores.
  const [{ data: submissions }, { data: scoreRows }] = await Promise.all([
    supabase
      .from("submissions")
      .select(
        "id, deliverable_id, team_id, competition_id, file_url, file_name, file_size_bytes, submitted_at, deliverables(name, sequence, is_locked_gate, due_at), teams(name)"
      )
      .in("competition_id", competitionIds),
    supabase
      .from("submission_scores")
      .select("submission_id, score, passed_gate, status, updated_at, judge_id")
      .in("competition_id", competitionIds)
      .in("judge_id", judgeIds),
  ]);

  if (!submissions) return [];

  const scoreBySubmission = new Map<string, RawScoreRow>();
  for (const s of (scoreRows ?? []) as RawScoreRow[]) {
    scoreBySubmission.set(s.submission_id, s);
  }

  return (submissions as RawSubmissionRow[]).map((row) => {
    const del = unwrapOne(row.deliverables);
    const team = unwrapOne(row.teams);
    const meta = compMeta.get(row.competition_id);
    const mine = scoreBySubmission.get(row.id);
    return {
      submission_id: row.id,
      competition_id: row.competition_id,
      competition_name: meta?.competition_name ?? "Competition",
      company_name: meta?.company_name ?? "Company",
      deliverable_id: row.deliverable_id,
      deliverable_name: del?.name ?? "",
      deliverable_sequence: del?.sequence ?? 0,
      deliverable_is_locked_gate: del?.is_locked_gate ?? false,
      deliverable_due_at: del?.due_at ?? null,
      team_id: row.team_id,
      team_name: team?.name ?? "Team",
      file_url: row.file_url,
      file_name: row.file_name,
      file_size_bytes: row.file_size_bytes,
      submitted_at: row.submitted_at,
      my_score: mine?.score ?? null,
      my_passed_gate: mine?.passed_gate ?? null,
      my_score_status: mine?.status ?? null,
      my_score_updated_at: mine?.updated_at ?? null,
    } satisfies JudgeSubmissionQueueRow;
  });
}
